import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { makeStyles, fade } from '@material-ui/core/styles';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	IconButton,
	Button,
	Typography,
	Tooltip,
	Grow
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import clsx from 'clsx';
import AsyncItemSearch from '../../../Components/AsyncItemSearch';
import { axiosDefaultConfig } from '../../../Services/API_Service';

const useStyles = makeStyles((theme) => ({
	body: {
		padding: theme.spacing(2)
	},
	searchRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5)
	},
	hint: {
		color: theme.palette.text.disabled,
		fontSize: 12.5
	},
	headCell: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: theme.palette.text.secondary,
		padding: theme.spacing(0.75, 1.5)
	},
	cell: {
		padding: theme.spacing(0.75, 1.5)
	},
	mono: {
		fontFamily: 'monospace',
		fontSize: 12.5
	},
	normalPrice: {
		color: theme.palette.text.secondary,
		fontVariantNumeric: 'tabular-nums'
	},
	promoInput: {
		width: 110,
		'& .MuiOutlinedInput-root': {
			backgroundColor: fade('#FEF200', theme.palette.type === 'dark' ? 0.08 : 0.18)
		}
	},
	deleteBtn: {
		padding: 6,
		color: theme.palette.text.secondary,
		'&:hover': {
			color: theme.palette.error.main,
			backgroundColor: fade(theme.palette.error.main, 0.12)
		}
	},
	empty: {
		padding: theme.spacing(2, 0),
		color: theme.palette.text.disabled,
		fontStyle: 'italic',
		fontSize: 13
	},
	sectionError: {
		color: theme.palette.error.main,
		fontSize: 12.5,
		marginTop: theme.spacing(1)
	}
}));

export default function SkuSection({ form, onFormChange, client, language, errors }) {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();
	const [showSearch, setShowSearch] = useState(false);

	const sectionErrors = (errors || []).filter((err) => err.section === 2);

	const onPick = (item) => {
		if (!item) {
			return;
		}
		if (form.skus.some((s) => String(s.product_id) === String(item.product_id))) {
			enqueueSnackbar(language.sku_already_added, { variant: 'warning' });
			return;
		}
		onFormChange((prev) => ({
			...prev,
			skus: [...prev.skus, {
				id: '',
				product_id: item.product_id,
				product_code: item.product_code || '',
				product_name: item.product_name || '',
				normal_price: item.default_price === null || item.default_price === undefined ? '' : String(item.default_price),
				promo_price: ''
			}]
		}));
		setShowSearch(false);
	};

	const onPromoPrice = (productId) => (e) => {
		const value = e.target.value;
		onFormChange((prev) => ({
			...prev,
			skus: prev.skus.map((s) => (String(s.product_id) === String(productId) ? { ...s, promo_price: value } : s))
		}));
	};

	const onRemove = (productId) => () => {
		onFormChange((prev) => ({
			...prev,
			skus: prev.skus.filter((s) => String(s.product_id) !== String(productId)),
			tasks: prev.tasks.map((t) => ({
				...t,
				task_skus: t.task_skus.filter((pid) => String(pid) !== String(productId))
			}))
		}));
	};

	const priceWarn = (sku) => {
		if (sku.promo_price === '' || sku.normal_price === '') {
			return false;
		}
		return parseFloat(sku.promo_price) >= parseFloat(sku.normal_price);
	};

	return (
		<div className={classes.body}>
			<div className={classes.searchRow}>
				{showSearch ? (
					<AsyncItemSearch
						width={360}
						language={language}
						axiosConfig={axiosDefaultConfig(client)}
						extraParams={{ brand_id: form.brand_id }}
						onItemSelect={onPick}
					/>
				) : (
					<Tooltip title={!form.brand_id ? language.select_brand_first : ''} arrow>
						<span>
							<Button
								variant="outlined"
								size="small"
								startIcon={<AddRoundedIcon />}
								disabled={!form.brand_id}
								onClick={() => setShowSearch(true)}>
								{language.add_sku}
							</Button>
						</span>
					</Tooltip>
				)}
				{!form.brand_id && (
					<Typography className={classes.hint}>{language.select_brand_first}</Typography>
				)}
			</div>

			{form.skus.length === 0 ? (
				<Typography className={classes.empty}>{language.no_data || 'No SKUs added'}</Typography>
			) : (
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell className={classes.headCell}>{language.product_code || 'SKU'}</TableCell>
							<TableCell className={classes.headCell}>{language.product_name || 'Product Name'}</TableCell>
							<TableCell className={classes.headCell}>{language.normal_price}</TableCell>
							<TableCell className={classes.headCell}>{language.promo_price}</TableCell>
							<TableCell className={classes.headCell} />
						</TableRow>
					</TableHead>
					<TableBody>
						{form.skus.map((sku) => (
							<Grow in key={sku.product_id} timeout={240}>
								<TableRow>
									<TableCell className={classes.cell}>
										<span className={classes.mono}>{sku.product_code}</span>
									</TableCell>
									<TableCell className={classes.cell}>{sku.product_name}</TableCell>
									<TableCell className={clsx(classes.cell, classes.normalPrice)}>
										{sku.normal_price === '' ? '—' : `RM ${parseFloat(sku.normal_price).toFixed(2)}`}
									</TableCell>
									<TableCell className={classes.cell}>
										<TextField
											type="number"
											variant="outlined"
											size="small"
											className={classes.promoInput}
											value={sku.promo_price}
											error={priceWarn(sku)}
											helperText={priceWarn(sku) ? language.promo_price_warning : ''}
											onChange={onPromoPrice(sku.product_id)}
											inputProps={{ step: '0.01', min: '0' }}
										/>
									</TableCell>
									<TableCell className={classes.cell} align="right">
										<IconButton className={classes.deleteBtn} onClick={onRemove(sku.product_id)}>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							</Grow>
						))}
					</TableBody>
				</Table>
			)}
			{sectionErrors.map((err) => (
				<Typography key={err.message} className={classes.sectionError}>{err.message}</Typography>
			))}
		</div>
	);
}
